import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Globe, FileUp, Sparkles, Loader2, Check, AlertCircle, Wand2,
  ChevronRight, Eye, FileText, ChevronLeft, Send, Plus, Trash2, Award,
  Combine, ShieldCheck,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AdminAPI } from '@/lib/admin-api-ext';
import { ConfidenceMeter, requiresReview } from '@/components/admin/ConfidenceMeter';
import { CapabilityToggles, DEFAULT_CARD_CAPABILITIES } from '@/components/admin/CapabilityToggles';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from '@/lib/format-time';

const v = (node) => (node && typeof node === 'object' && 'value' in node ? node.value : node);
const c = (node) => (node && typeof node === 'object' && 'confidence' in node ? Number(node.confidence) : 1);

const FieldRow = ({ label, node, onChange, type = 'text', options = null, testid }) => {
  const val = v(node);
  const conf = c(node);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 items-start sm:items-center py-1.5" data-testid={testid}>
      <Label className={cn('text-[11px] uppercase tracking-[0.14em] text-muted-foreground', requiresReview(conf) && 'text-rose-500')}>{label}</Label>
      <div>
        {options ? (
          <Select value={val ?? ''} onValueChange={(nv) => onChange({ ...(node || {}), value: nv })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ) : type === 'textarea' ? (
          <Textarea rows={2} value={val ?? ''} onChange={(e) => onChange({ ...(node || {}), value: e.target.value })} className="text-xs" />
        ) : type === 'number' ? (
          <Input type="number" value={val ?? ''} onChange={(e) => onChange({ ...(node || {}), value: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 font-mono-num text-xs" />
        ) : type === 'bool' ? (
          <Select value={(val === true || val === 'true') ? 'true' : 'false'} onValueChange={(nv) => onChange({ ...(node || {}), value: nv === 'true' })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="true">true</SelectItem><SelectItem value="false">false</SelectItem></SelectContent>
          </Select>
        ) : (
          <Input value={val ?? ''} onChange={(e) => onChange({ ...(node || {}), value: e.target.value })} className="h-8 text-xs" />
        )}
      </div>
      <ConfidenceMeter value={conf} size="xs" />
    </div>
  );
};

const RuleReviewCard = ({ rule, index, onChange, onRemove }) => {
  const set = (patch) => onChange({ ...rule, ...patch });
  const setMatch = (patch) => set({ match: { ...(rule.match || {}), ...patch } });
  const setEarn = (patch) => set({ earn: { ...(rule.earn || {}), ...patch } });
  const conf = Number(rule.confidence) || 0;
  const flagged = requiresReview(conf);
  return (
    <Card className={cn('p-3', flagged ? 'border-amber-500/40 bg-amber-500/5' : 'border-border')} data-testid={`admin-ingest-rule-${index}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Badge variant="outline" className="text-[10px] font-mono-num">#{index + 1}</Badge>
        <Input value={rule.label || ''} onChange={(e) => set({ label: e.target.value })} placeholder="Rule label" className="h-8 text-xs font-medium flex-1 min-w-[180px]" />
        <ConfidenceMeter value={conf} />
        {rule.exclude && <Badge className="text-[10px] bg-rose-500/10 text-rose-600 border-rose-500/30">exclusion</Badge>}
        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>
      </div>
      {rule._text_snippet && (
        <div className="text-[10px] text-muted-foreground italic mb-2 px-2 py-1 rounded bg-[hsl(var(--surface-2))] border border-border">“{rule._text_snippet}”</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Priority</Label>
          <Input type="number" value={rule.priority ?? 10} onChange={(e) => set({ priority: Number(e.target.value) || 0 })} className="h-7 font-mono-num text-xs" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mode</Label>
          <Select value={rule.match?.mode || 'any'} onValueChange={(nv) => setMatch({ mode: nv })}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="any">any</SelectItem><SelectItem value="online">online</SelectItem><SelectItem value="offline">offline</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Categories / MCC / merchants</Label>
          <div className="text-[11px] mt-1 truncate">
            {(rule.match?.categories || []).join(', ') || '—'}
            {(rule.match?.mcc?.length) ? <span className="text-muted-foreground"> · MCC: {rule.match.mcc.join(', ')}</span> : null}
            {(rule.match?.merchants?.length) ? <span className="text-muted-foreground"> · {rule.match.merchants.join(', ')}</span> : null}
          </div>
        </div>
        {!rule.exclude && rule.earn && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Rate</Label>
              <Input type="number" value={rule.earn.rate ?? 0} onChange={(e) => setEarn({ rate: Number(e.target.value) || 0 })} className="h-7 font-mono-num text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Per ₹</Label>
              <Input type="number" value={rule.earn.per ?? 100} onChange={(e) => setEarn({ per: Number(e.target.value) || 1 })} className="h-7 font-mono-num text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Currency</Label>
              <Input value={rule.earn.currency || 'RP'} onChange={(e) => setEarn({ currency: e.target.value })} className="h-7 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pt value (₹)</Label>
              <Input type="number" step="0.01" value={rule.earn.point_value_inr ?? 1} onChange={(e) => setEarn({ point_value_inr: Number(e.target.value) || 0 })} className="h-7 font-mono-num text-xs" />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default function AdminIngestCard() {
  const { jobId: urlJobId } = useParams();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [job, setJob] = useState(null);
  const [card, setCard] = useState(null);
  const [rules, setRules] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [capabilities, setCapabilities] = useState(DEFAULT_CARD_CAPABILITIES);
  const [applying, setApplying] = useState(false);

  // P1.2 — Dedup state. When the operator clicks Apply, we first hit
  // /match-candidates to see if this looks like a card we already have. If
  // there are matches, we show a chooser dialog (merge into existing OR
  // create a fresh card).
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupCandidates, setDedupCandidates] = useState([]);

  // Wave B — PDF + Bulk URL state
  const [pdfFile, setPdfFile] = useState(null);
  const [bulkText, setBulkText] = useState('');
  const [bulkForce, setBulkForce] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);          // {created, duplicates, errors}
  const [queuedJob, setQueuedJob] = useState(null);            // job currently being polled before review

  useEffect(() => {
    if (!urlJobId) return;
    AdminAPI.ingest.get(urlJobId).then((j) => {
      setJob(j);
      hydrate(j);
    }).catch((e) => toast.error(e.message || 'Failed to load ingestion job'));
  }, [urlJobId]);

  // Wave B: poll a queued/running job until it reaches a terminal status. When
  // it lands at `ready`, navigate to the review pane; otherwise surface the
  // failure inline so the operator sees it.
  useEffect(() => {
    if (!queuedJob || ['ready', 'applied', 'failed', 'needs_manual_review'].includes(queuedJob.status)) {
      if (queuedJob && queuedJob.status === 'ready') {
        navigate(`/admin/ingest/${queuedJob.id}`);
      }
      return;
    }
    const t = setTimeout(async () => {
      try {
        const next = await AdminAPI.ingest.get(queuedJob.id);
        setQueuedJob(next);
      } catch (e) {
        toast.error(e.message || 'Polling failed');
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [queuedJob, navigate]);

  const hydrate = (j) => {
    const ex = j?.extracted || {};
    setCard(ex.card || {});
    setRules(ex.rules || []);
    setMilestones(ex.milestones || []);
    const meta = j?.normalized_entities || {};
    const caps = {
      has_fee_waiver: !!v(ex.card?.fee_waiver_inr) || !!v(ex.card?.fee_waiver_logic),
      has_lounge_access: !!v(ex.card?.has_lounge_access) || !!(v(ex.card?.lounge_access) && (v(ex.card?.lounge_access).domestic_per_quarter || v(ex.card?.lounge_access).international_per_year)),
      supports_transfer_partners: !!v(ex.card?.supports_transfer_partners) || ((v(ex.card?.transfer_partners) || []).length > 0),
      has_milestones: !!v(ex.card?.has_milestones) || ((ex.milestones || []).length > 0),
      supports_portal_accelerators: (ex.rules || []).some((r) => (r.match?.merchants || []).some((m) => /smartbuy|edge|gyftr/i.test(m))),
      is_business_card: /business|corporate/i.test((v(ex.card?.name) || '') + (v(ex.card?.tier) || '')),
    };
    setCapabilities(caps);
  };

  const startCrawl = async () => {
    if (!url || !url.startsWith('http')) {
      toast.error('Enter a valid http(s) URL');
      return;
    }
    setCrawling(true);
    try {
      // Wave B: this endpoint now returns a `queued` job immediately. The UI
      // polls until status reaches a terminal state (then it navigates to the
      // review pane when ready).
      const j = await AdminAPI.ingest.url(url);
      setQueuedJob(j);
    } catch (e) {
      toast.error(e.message || 'Ingestion failed');
    } finally {
      setCrawling(false);
    }
  };

  // Wave B: PDF upload — client-side 20 MB guard, then enqueue + poll.
  const startPdfUpload = async () => {
    if (!pdfFile) {
      toast.error('Pick a PDF first');
      return;
    }
    if (!/\.pdf$/i.test(pdfFile.name)) {
      toast.error('Only .pdf files are accepted');
      return;
    }
    const HARD_CAP_BYTES = 20 * 1024 * 1024;
    if (pdfFile.size > HARD_CAP_BYTES) {
      toast.error(`PDF is ${(pdfFile.size / (1024 * 1024)).toFixed(1)} MB; hard cap is 20 MB`);
      return;
    }
    setCrawling(true);
    try {
      const j = await AdminAPI.ingest.pdf(pdfFile);
      setQueuedJob(j);
    } catch (e) {
      toast.error(e.message || 'PDF upload failed');
    } finally {
      setCrawling(false);
    }
  };

  // Wave B: Bulk URL paste — split on newline / whitespace, submit with soft
  // dedupe. Show a per-URL report after the call.
  const startBulkSubmit = async () => {
    const urls = bulkText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!urls.length) {
      toast.error('Paste at least one URL');
      return;
    }
    setCrawling(true);
    try {
      const report = await AdminAPI.ingest.urlsBulk(urls, bulkForce);
      setBulkReport(report);
      if (report.created?.length) {
        toast.success(`Queued ${report.created.length} new ingestion job(s)`);
      }
      if (report.duplicates?.length) {
        toast.info(`${report.duplicates.length} URL(s) already had jobs — see report below`);
      }
    } catch (e) {
      toast.error(e.message || 'Bulk submission failed');
    } finally {
      setCrawling(false);
    }
  };

  // P1.2 — Dedup-aware apply. Step 1: probe for match candidates. If we
  // find any, surface the modal; otherwise apply as a new card.
  const apply = async () => {
    if (!job) return;
    setDedupLoading(true);
    try {
      const probe = await AdminAPI.ingest.matchCandidates(job.id, 0.7, 5);
      const matches = probe?.matches || [];
      if (matches.length > 0) {
        setDedupCandidates(matches);
        setDedupOpen(true);
        return;
      }
      await applyWithMode({ merge_into_card_id: null });
    } catch (e) {
      // Soft fail — if dedup probe errors out (e.g. legacy job without
      // extracted.card.bank), fall back to the create-new path.
      console.warn('[apply] dedup probe failed, falling back to create-new:', e);
      await applyWithMode({ merge_into_card_id: null });
    } finally {
      setDedupLoading(false);
    }
  };

  const applyWithMode = async ({ merge_into_card_id }) => {
    if (!job) return;
    setApplying(true);
    try {
      const cardWithCaps = {
        ...card,
        capabilities,
      };
      const res = await AdminAPI.ingest.apply(job.id, {
        card: cardWithCaps,
        rules,
        milestones,
        merge_into_card_id: merge_into_card_id || undefined,
      });
      if (merge_into_card_id) {
        toast.success(
          `Merged into existing card · ${res.rules_added || 0} new rule(s) appended (now ${res.rules_total || 0} total). Review the new draft before publishing.`,
        );
      } else {
        toast.success('Card created · draft v1 saved · open the rules editor to publish.');
      }
      setDedupOpen(false);
      navigate(`/admin/cards/${res.card_id}/rules`);
    } catch (e) {
      toast.error(e.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  // Step 1: paste URL
  if (!job && !urlJobId) {
    return (
      <div className="space-y-4" data-testid="admin-ingest-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Catalog · AI Ingestion</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Ingest a card</h1>
          <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
            Paste the issuer's card URL, upload an issuer PDF (T&Cs / MITC), or queue many URLs at once. We crawl/parse, run a GPT-5 structured-extraction pass with confidence scoring, and produce a reviewable draft — no manual schema entry.
          </p>
        </div>

        {/* Wave B: live progress card while a queued job runs */}
        {queuedJob && (
          <Card className="p-3 border-primary/40 bg-primary/5 flex items-center gap-3" data-testid="admin-ingest-queued-card">
            <Loader2 className={cn('h-5 w-5', !['ready', 'applied', 'failed', 'needs_manual_review'].includes(queuedJob.status) && 'animate-spin', queuedJob.status === 'failed' && 'text-rose-500')} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium flex items-center gap-2 flex-wrap">
                Ingestion job <span className="font-mono-num">{queuedJob.id}</span>
                <Badge variant="outline" className="text-[10px]">{queuedJob.status}</Badge>
                <Badge variant="outline" className="text-[10px]">{queuedJob.progress || '—'}</Badge>
                {queuedJob.crawler_used && <Badge variant="outline" className="text-[10px]">{queuedJob.crawler_used}</Badge>}
              </div>
              {queuedJob.status === 'failed' && (
                <div className="mt-1 text-[11px] text-rose-600">{queuedJob.error || 'Ingestion failed.'}</div>
              )}
              {queuedJob.status === 'needs_manual_review' && (
                <div className="mt-1 text-[11px] text-amber-600">{queuedJob.error || 'No usable text extracted — manual review needed.'}</div>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setQueuedJob(null)} data-testid="admin-ingest-queued-dismiss">Dismiss</Button>
          </Card>
        )}

        <Tabs defaultValue="url">
          <TabsList>
            <TabsTrigger value="url" data-testid="admin-ingest-tab-url"><Globe className="h-3.5 w-3.5 mr-1.5" /> URL</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="admin-ingest-tab-pdf"><FileUp className="h-3.5 w-3.5 mr-1.5" /> PDF</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="admin-ingest-tab-bulk"><FileText className="h-3.5 w-3.5 mr-1.5" /> Bulk URLs</TabsTrigger>
          </TabsList>
          <TabsContent value="url" className="mt-4">
            <Card className="p-4">
              <Label className="text-xs">Card landing / rewards / MITC URL</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.hdfcbank.com/personal/pay/cards/credit-cards/infinia-credit-card"
                  className="h-10 font-mono-num text-xs"
                  onKeyDown={(e) => e.key === 'Enter' && startCrawl()}
                  data-testid="admin-ingest-url-input"
                />
                <Button onClick={startCrawl} disabled={crawling || !!queuedJob} className="gap-1.5 px-4" data-testid="admin-ingest-url-submit">
                  {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {crawling ? 'Queueing…' : 'Queue ingestion'}
                </Button>
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">1 · Crawl</div>native httpx + readability — Firecrawl fallback ready</div>
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">2 · Extract</div>GPT-5 structured JSON output with per-field confidence</div>
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">3 · Review</div>Apply only after operator validates and runs simulator</div>
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                <Link to="/admin/ingestion-jobs" className="hover:text-foreground inline-flex items-center gap-1" data-testid="admin-ingest-history-link">
                  <FileText className="h-3 w-3" /> See all ingestion jobs
                </Link>
              </div>
            </Card>
          </TabsContent>

          {/* Wave B: PDF tab */}
          <TabsContent value="pdf" className="mt-4">
            <Card className="p-4 space-y-3">
              <div>
                <Label className="text-xs">Issuer PDF (T&Cs, MITC, product brochure)</Label>
                <div className="mt-1 flex gap-2 items-center flex-wrap">
                  <Input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="h-10 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1 file:text-foreground file:text-xs"
                    data-testid="admin-ingest-pdf-input"
                  />
                  <Button onClick={startPdfUpload} disabled={crawling || !pdfFile || !!queuedJob} className="gap-1.5 px-4" data-testid="admin-ingest-pdf-submit">
                    {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {crawling ? 'Uploading…' : 'Upload & extract'}
                  </Button>
                </div>
                {pdfFile && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Selected: <span className="font-mono-num">{pdfFile.name}</span> · {(pdfFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">1 · pdfplumber</div>Native text extraction first — fast & free for text PDFs.</div>
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">2 · Vision fallback</div>Scanned PDFs → GPT-5 Vision page OCR (first 10 pages).</div>
                <div className="rounded border border-border px-2.5 py-2"><div className="font-medium text-foreground">3 · Extract & review</div>Same structured-JSON output + operator review pane.</div>
              </div>
              <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2.5 py-2 text-[10px] text-amber-700 dark:text-amber-400 flex gap-2">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                Hard cap: <b>20 MB</b>. Vision pages beyond <b>10</b> are skipped and noted on the job.
              </div>
            </Card>
          </TabsContent>

          {/* Wave B: Bulk URLs tab */}
          <TabsContent value="bulk" className="mt-4">
            <Card className="p-4 space-y-3">
              <div>
                <Label className="text-xs">Paste many URLs (one per line)</Label>
                <Textarea
                  rows={8}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={'https://www.hdfcbank.com/.../infinia-credit-card\nhttps://www.axisbank.com/.../atlas-credit-card\nhttps://www.sbicard.com/.../prime-card'}
                  className="mt-1 font-mono-num text-[11px]"
                  data-testid="admin-ingest-bulk-input"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={bulkForce}
                    onChange={(e) => setBulkForce(e.target.checked)}
                    className="h-3.5 w-3.5"
                    data-testid="admin-ingest-bulk-force"
                  />
                  Re-ingest even if a job already exists for the URL
                </label>
                <Button onClick={startBulkSubmit} disabled={crawling || !bulkText.trim()} className="ml-auto gap-1.5 px-4" data-testid="admin-ingest-bulk-submit">
                  {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {crawling ? 'Queueing…' : 'Queue batch'}
                </Button>
              </div>
              {bulkReport && (
                <div className="rounded border border-border bg-[hsl(var(--surface-2))] p-3 space-y-2" data-testid="admin-ingest-bulk-report">
                  <div className="text-[11px] font-medium">Batch report</div>
                  <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
                    <div className="rounded border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5">
                      <div className="font-medium text-emerald-700 dark:text-emerald-400">Queued ({bulkReport.created.length})</div>
                      <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {bulkReport.created.map((c, i) => (
                          <li key={i} className="truncate" title={c.url}><Link to={`/admin/ingest/${c.job_id}`} className="hover:underline">{c.url}</Link></li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                      <div className="font-medium text-amber-700 dark:text-amber-400">Duplicates ({bulkReport.duplicates.length})</div>
                      <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {bulkReport.duplicates.map((d, i) => (
                          <li key={i} className="truncate" title={d.url}>
                            {d.existing_job_id ? <Link to={`/admin/ingest/${d.existing_job_id}`} className="hover:underline">{d.url}</Link> : d.url}
                            <span className="text-muted-foreground"> — {d.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded border border-rose-500/30 bg-rose-500/5 px-2 py-1.5">
                      <div className="font-medium text-rose-700 dark:text-rose-400">Errors ({bulkReport.errors.length})</div>
                      <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {bulkReport.errors.map((e, i) => (
                          <li key={i} className="truncate" title={e.url}>{e.url} — {e.message}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Soft-dedupe: existing jobs are linked (click to open). Tick <b>Re-ingest</b> above and resubmit to force a fresh crawl.
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  if (urlJobId && !job) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <div className="grid lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] gap-3">
          <Skeleton className="h-[420px] w-full" />
          <Skeleton className="h-[420px] w-full" />
        </div>
      </div>
    );
  }

  const meta = job?.extracted?._meta || {};
  const overallConf = meta.overall_confidence ?? 0;
  const isNotCard = meta.is_credit_card_page === false;

  return (
    <div className="space-y-3" data-testid="admin-ingest-review-page">
      <div className="flex items-start justify-between gap-3 flex-col sm:flex-row">
        <div className="min-w-0">
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground">
            <Link to="/admin/ingestion-jobs"><ChevronLeft className="h-3.5 w-3.5" /> Jobs</Link>
          </Button>
          <h1 className="font-display text-xl font-semibold tracking-tight mt-1">Review ingestion · {v(card?.name) || 'Untitled card'}</h1>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            <span className="font-mono-num">{job.id}</span>
            <span>·</span>
            <Badge variant="outline" className="text-[10px] gap-1"><Globe className="h-3 w-3" /> {job.crawler_used || '—'}</Badge>
            <span>· {job.raw_text_length} chars</span>
            {job.source_url && <a href={job.source_url} target="_blank" rel="noreferrer" className="hover:text-foreground truncate max-w-md inline-flex items-center gap-1"><ChevronRight className="h-3 w-3" /> {job.source_url}</a>}
            <span>·</span>
            <span>created {formatDistanceToNow(job.created_at)}</span>
            <span>·</span>
            <ConfidenceMeter value={overallConf} label="overall" />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="admin-ingest-new">
            <Link to="/admin/ingest"><Plus className="h-3.5 w-3.5" /> Ingest another</Link>
          </Button>
          <Button size="sm" onClick={apply} disabled={applying || dedupLoading || job.status === 'applied' || isNotCard} className="gap-1.5" data-testid="admin-ingest-apply">
            {(applying || dedupLoading) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {job.status === 'applied' ? 'Already applied' : (dedupLoading ? 'Checking for duplicates…' : 'Apply → create draft card')}
          </Button>
        </div>
      </div>

      {isNotCard && (
        <Card className="p-3 border-amber-500/40 bg-amber-500/5 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5" />
          The AI flagged this page as <b>not a credit card page</b>. Apply is disabled. Try a different URL.
        </Card>
      )}

      {job.status === 'applied' && (
        <Card className="p-3 border-emerald-500/40 bg-emerald-500/5 flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <Check className="h-3.5 w-3.5" />
          Applied · card <span className="font-mono-num font-medium">{job.applied_card_id}</span> created with draft v1.
          <Link to={`/admin/cards/${job.applied_card_id}/rules`} className="ml-auto underline">Open rules editor →</Link>
        </Card>
      )}

      <div className="grid xl:grid-cols-[minmax(0,440px)_minmax(0,1fr)_minmax(0,340px)] gap-3">
        {/* LEFT: raw text (auditable artifact) */}
        <Card className="p-3 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Raw crawled text</div>
            <Badge variant="outline" className="text-[9px]">{job.raw_text_length} chars</Badge>
          </div>
          <div
            className="flex-1 max-h-[600px] overflow-y-auto scrollbar-thin rounded border border-border bg-[hsl(var(--surface-2))]"
            data-testid="admin-ingest-raw-text-scroll"
          >
            <pre className="p-2 text-[10px] font-mono-num leading-relaxed whitespace-pre-wrap break-words text-muted-foreground">
              {job.raw_text || '—'}
            </pre>
          </div>
        </Card>

        {/* CENTER: card metadata + rules */}
        <div className="space-y-3 min-w-0">
          <Card className="p-3">
            <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-accent" /> Card metadata</div>
            <div className="space-y-0.5">
              <FieldRow testid="admin-ingest-field-bank" label="Bank" node={card?.bank} onChange={(n) => setCard((x) => ({ ...x, bank: n }))} />
              <FieldRow testid="admin-ingest-field-name" label="Card name" node={card?.name} onChange={(n) => setCard((x) => ({ ...x, name: n }))} />
              <FieldRow testid="admin-ingest-field-tier" label="Tier" node={card?.tier} onChange={(n) => setCard((x) => ({ ...x, tier: n }))} />
              <div className="grid grid-cols-2 gap-x-3">
                <FieldRow label="Network" node={card?.network} options={['VISA', 'MASTERCARD', 'AMEX', 'RUPAY', 'DINERS']} onChange={(n) => setCard((x) => ({ ...x, network: n }))} />
                <FieldRow label="Type" node={card?.type} options={['rewards', 'cashback', 'miles']} onChange={(n) => setCard((x) => ({ ...x, type: n }))} />
                <FieldRow label="Category" node={card?.category} options={['rewards','cashback','travel','premium','lifestyle','co-brand']} onChange={(n) => setCard((x) => ({ ...x, category: n }))} />
                <FieldRow label="Currency" node={card?.currency} onChange={(n) => setCard((x) => ({ ...x, currency: n }))} />
                <FieldRow label="Annual fee ₹" type="number" node={card?.annual_fee} onChange={(n) => setCard((x) => ({ ...x, annual_fee: n }))} />
                <FieldRow label="Joining fee ₹" type="number" node={card?.joining_fee} onChange={(n) => setCard((x) => ({ ...x, joining_fee: n }))} />
                <FieldRow label="Fee waiver ₹" type="number" node={card?.fee_waiver_inr} onChange={(n) => setCard((x) => ({ ...x, fee_waiver_inr: n }))} />
                <FieldRow label="Pt value ₹" type="number" node={card?.point_value_inr} onChange={(n) => setCard((x) => ({ ...x, point_value_inr: n }))} />
              </div>
              <FieldRow label="Fee waiver logic" type="textarea" node={card?.fee_waiver_logic} onChange={(n) => setCard((x) => ({ ...x, fee_waiver_logic: n }))} />
              <FieldRow label="Welcome bonus" type="textarea" node={card?.welcome_bonus} onChange={(n) => setCard((x) => ({ ...x, welcome_bonus: n }))} />
            </div>
          </Card>

          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5 text-primary" /> AI-extracted rules <Badge variant="outline" className="ml-1 text-[9px]">{rules.length}</Badge></div>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setRules((rs) => [...rs, { id: `rule-${Math.random().toString(16).slice(2,8)}`, label: 'Custom rule', priority: 10, exclude: false, match: { categories: [], mcc: [], merchants: [], mode: 'any', international: null }, earn: { type: 'rate_per_spend', currency: 'RP', rate: 1, per: 100, point_value_inr: 1 }, cap: null, confidence: 1.0, _text_snippet: 'Operator-added rule' }])}>
                <Plus className="h-3 w-3 mr-1" /> Add rule
              </Button>
            </div>
            <div className="space-y-2">
              {rules.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-md">
                  No rules extracted. Add manually or try a different URL.
                </div>
              ) : rules.map((r, i) => (
                <RuleReviewCard key={r.id || i} rule={r} index={i} onChange={(next) => setRules((rs) => rs.map((x, k) => k === i ? next : x))} onRemove={() => setRules((rs) => rs.filter((_, k) => k !== i))} />
              ))}
            </div>
          </Card>

          {milestones.length > 0 && (
            <Card className="p-3">
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Award /> Milestones <Badge variant="outline" className="ml-1 text-[9px]">{milestones.length}</Badge></div>
              <div className="space-y-2">
                {milestones.map((m, i) => (
                  <div key={m.id || i} className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Input value={m.name || ''} onChange={(e) => setMilestones((ms) => ms.map((x, k) => k === i ? { ...x, name: e.target.value } : x))} className="h-7 text-xs font-medium flex-1" />
                      <ConfidenceMeter value={m.confidence} />
                    </div>
                    {m._text_snippet && <div className="text-[10px] text-muted-foreground italic mt-1">“{m._text_snippet}”</div>}
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div><Label className="text-[10px]">Target ₹</Label><Input type="number" value={m.target_inr ?? 0} onChange={(e) => setMilestones((ms) => ms.map((x, k) => k === i ? { ...x, target_inr: Number(e.target.value) || 0 } : x))} className="h-7 font-mono-num text-xs" /></div>
                      <div><Label className="text-[10px]">Reward</Label><Input value={m.reward || ''} onChange={(e) => setMilestones((ms) => ms.map((x, k) => k === i ? { ...x, reward: e.target.value } : x))} className="h-7 text-xs" /></div>
                      <div><Label className="text-[10px]">₹ value</Label><Input type="number" value={m.reward_value_inr ?? 0} onChange={(e) => setMilestones((ms) => ms.map((x, k) => k === i ? { ...x, reward_value_inr: Number(e.target.value) || 0 } : x))} className="h-7 font-mono-num text-xs" /></div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT: capabilities + linked entities */}
        <div className="space-y-3">
          <Card className="p-3">
            <div className="text-xs font-semibold mb-2">Detected capabilities</div>
            <p className="text-[10px] text-muted-foreground mb-2">AI seed values — toggle to force visibility/hiding of sections after apply.</p>
            <CapabilityToggles value={capabilities} onChange={setCapabilities} compact />
          </Card>
          <Card className="p-3">
            <div className="text-xs font-semibold mb-2">Linked entities</div>
            <p className="text-[10px] text-muted-foreground mb-2">Auto-resolved against centralized libraries.</p>
            {Object.entries(job.normalized_entities || {}).map(([group, items]) => (
              <div key={group} className="mb-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{group}</div>
                {(items || []).length === 0 ? (
                  <div className="text-[11px] text-muted-foreground">—</div>
                ) : (
                  <div className="space-y-1">
                    {items.map((it, i) => (
                      <div key={i} className="text-[11px] flex items-center justify-between rounded border border-border bg-[hsl(var(--surface-2))] px-2 py-1">
                        <span className="truncate">{it.input}</span>
                        {it.resolved_id ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30 gap-1"><Check className="h-2.5 w-2.5" /> {it.resolved_name}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/30">unresolved</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
          {(job.extracted?.unmatched_signals || []).length > 0 && (
            <Card className="p-3">
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Unmatched signals</div>
              <ul className="space-y-1 text-[11px] text-muted-foreground list-disc list-inside">
                {(job.extracted.unmatched_signals || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Card>
          )}
        </div>
      </div>

      {/* P1.2 — Dedup dialog */}
      <Dialog open={dedupOpen} onOpenChange={(v) => !applying && setDedupOpen(v)}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-ingest-dedup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-500" /> Possible duplicate detected
            </DialogTitle>
            <DialogDescription className="text-xs">
              <b>{card?.bank?.value || card?.bank}</b> {(card?.name?.value || card?.name) ? <>&middot; <b>{card?.name?.value || card?.name}</b></> : null} looks similar to {dedupCandidates.length === 1 ? 'a card' : `${dedupCandidates.length} cards`} already in the library.
              Merge the extracted rules into an existing card, or apply as a brand-new variant.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="admin-ingest-dedup-list">
            {dedupCandidates.map((c) => (
              <div key={c.card_id} className="rounded-lg border border-border bg-[hsl(var(--surface-2))] p-3 flex items-start gap-3" data-testid={`admin-ingest-dedup-candidate-${c.card_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{c.bank} &mdash; {c.name}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="font-mono-num">{c.card_id}</span>
                    {c.tier && <Badge variant="outline" className="text-[9px]">{c.tier}</Badge>}
                    <span>{c.rule_count} rule{c.rule_count === 1 ? '' : 's'}</span>
                    <Badge className={c.is_high_confidence ? 'bg-amber-500/10 text-amber-600 border-amber-500/30 text-[9px]' : 'bg-sky-500/10 text-sky-600 border-sky-500/30 text-[9px]'}>
                      {Math.round(c.similarity * 100)}% match
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={applying}
                  onClick={() => applyWithMode({ merge_into_card_id: c.card_id })}
                  data-testid={`admin-ingest-dedup-merge-${c.card_id}`}
                >
                  {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Combine className="h-3.5 w-3.5" />}
                  Merge
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 flex-row mt-2">
            <Button
              variant="outline"
              onClick={() => applyWithMode({ merge_into_card_id: null })}
              disabled={applying}
              className="gap-1.5"
              data-testid="admin-ingest-dedup-create-new"
            >
              <Plus className="h-3.5 w-3.5" /> Create as new variant
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDedupOpen(false)}
              disabled={applying}
              data-testid="admin-ingest-dedup-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
